import { AsyncDirective } from 'lit/async-directive.js'
import { directive, ElementPart, PartInfo, PartType } from 'lit/directive.js'
import { asapScheduler, fromEvent, merge, Observable, Subject, Subscription, timer } from 'rxjs'
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators'
import { reducedMotion$ } from './reduced-motion'

export interface LongPressOptions {
	/** Hold time in ms before the long-press fires (default: 500) */
	duration?: number
	/** Movement in px that cancels a hold or disqualifies a tap (default: 10) */
	movementThreshold?: number
	/** Also fire on a double-click / double-tap (default: true) */
	doubleClick?: boolean
	/** Max ms between the two taps of a double-click (default: 400) */
	doubleClickWindow?: number
	/** Paint the in-element progress ring while the gesture builds (default: true) */
	indicator?: boolean
}

/**
 * In-element progress affordance — "Comet Ring + Aurora", scoped to the host's
 * own box so it scrolls / scales / leans with any component it's applied to.
 * The aurora blooms from the interior (behind the host's content) as the hold
 * builds; a comet arc laps the perimeter as the precise progress track. Commit
 * blooms both; the double-click cue pulses a sonar ring + aurora breathe.
 */
interface RingHandle {
	/** Fill 0→1 over `ms` (hold building toward fire). */
	fill(ms: number): void
	/** Confirm bloom on fire. */
	pulse(): void
	/** Hide and reset. */
	retract(): void
	/** Toggle the reduced-motion variant (static, no keyframes). */
	setReduced(reduced: boolean): void
	/** Remove the child. */
	destroy(): void
}

interface LongPressState {
	subscription: Subscription
	callback: () => void
	options: LongPressOptions
	/** Timestamp of the previous qualifying tap (0 = none pending). */
	lastTapAt: number
	ring: RingHandle | null
	/** Restore what setup wrote (host touch-action and position, every aria-keyshortcuts it added). */
	restore: () => void
}

/** The keyboard route to the act — advertised on whatever takes focus for the host. */
const KEY_SHORTCUT = 'Shift+F10'

/**
 * How long after a fired trigger is released its platform echo may still
 * arrive: Windows raises `contextmenu` just after the menu key comes up, and
 * Android raises one on a long touch.
 */
const CLAIM_GRACE_MS = 300

// Per-element state — keyed by element, NOT directive-instance fields, because
// `repeat` reuses the element across recreated directive instances.
const longPressMap = new WeakMap<Element, LongPressState>()

const SHEET_ID = 'schmancy-longpress-styles'
const RING_CSS = `
.schmancy-lp { --c: var(--schmancy-sys-color-primary-default, #6750A4); }
.schmancy-lp > i { position: absolute; inset: 0; border-radius: inherit; }
.schmancy-lp-aclip { overflow: hidden; }
.schmancy-lp-aurora {
  position: absolute; inset: -30%; border-radius: inherit;
  background:
    radial-gradient(40% 60% at 22% 30%, color-mix(in srgb, var(--c) 60%, transparent), transparent 70%),
    radial-gradient(50% 50% at 80% 72%, color-mix(in srgb, var(--lp-aurora-2, #22d3ee) 42%, transparent), transparent 70%),
    radial-gradient(45% 55% at 60% 18%, color-mix(in srgb, var(--lp-aurora-3, #ec4899) 38%, transparent), transparent 70%);
  filter: blur(12px);
  opacity: calc(0.1 + var(--lp-progress, 0) * 0.5);
  transform: scale(calc(0.9 + var(--lp-progress, 0) * 0.18));
  -webkit-mask: radial-gradient(circle at center, #000 calc(var(--lp-progress, 0) * 118%), transparent calc(var(--lp-progress, 0) * 118% + 12%));
          mask: radial-gradient(circle at center, #000 calc(var(--lp-progress, 0) * 118%), transparent calc(var(--lp-progress, 0) * 118% + 12%));
  animation: schmancy_lp_drift 6s ease-in-out infinite;
}
@keyframes schmancy_lp_drift { 0%, 100% { background-position: 0 0, 0 0, 0 0; } 50% { background-position: 14% 8%, -10% 0, 5% -8%; } }
.schmancy-lp-track {
  padding: 2px; background: color-mix(in srgb, var(--c) 20%, transparent);
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor; mask-composite: exclude;
  opacity: calc(min(var(--lp-progress, 0) * 6, 1) * 0.85);
}
.schmancy-lp-comet {
  padding: 2.5px;
  background: conic-gradient(from -90deg, transparent 0deg,
    color-mix(in srgb, var(--c) 0%, transparent) calc(var(--lp-progress, 0) * 360deg - 70deg),
    color-mix(in srgb, var(--c) 55%, transparent) calc(var(--lp-progress, 0) * 360deg - 30deg),
    var(--c) calc(var(--lp-progress, 0) * 360deg),
    transparent calc(var(--lp-progress, 0) * 360deg + 0.5deg));
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor; mask-composite: exclude;
  filter: drop-shadow(0 0 4px color-mix(in srgb, var(--c) 70%, transparent));
  opacity: min(var(--lp-progress, 0) * 8, 1);
}
.schmancy-lp-bloom { opacity: 0; box-shadow: 0 0 0 0 color-mix(in srgb, var(--c) 60%, transparent); }
.schmancy-lp.fire .schmancy-lp-comet { background: var(--c); opacity: 1; filter: drop-shadow(0 0 9px var(--c)); }
.schmancy-lp.fire .schmancy-lp-bloom { animation: schmancy_lp_bloom 520ms cubic-bezier(.22, 1, .36, 1) forwards; }
@keyframes schmancy_lp_bloom { 0% { opacity: .9; box-shadow: 0 0 0 0 color-mix(in srgb, var(--c) 65%, transparent); } 60% { opacity: .5; } 100% { opacity: 0; box-shadow: 0 0 0 16px transparent; } }
.schmancy-lp.fire .schmancy-lp-aurora { -webkit-mask: none; mask: none; animation: schmancy_lp_aflash 520ms ease-out; }
@keyframes schmancy_lp_aflash { 0% { opacity: .9; filter: blur(6px) brightness(1.7); transform: scale(1.1); } 100% { opacity: 0; filter: blur(28px); transform: scale(1.25); } }
.schmancy-lp.reduced .schmancy-lp-aurora,
.schmancy-lp.reduced .schmancy-lp-comet,
.schmancy-lp.reduced .schmancy-lp-bloom { animation: none; }
.schmancy-lp.reduced .schmancy-lp-comet { filter: none; }
.schmancy-lp.reduced .schmancy-lp-aurora { -webkit-mask: none; mask: none; transform: none; filter: blur(12px); }
`

/**
 * Inject the ring stylesheet into the host's OWN root (its ShadowRoot, or the
 * document head) if not already present there. A sheet in `document.head` does
 * NOT cross the shadow boundary of the tree the host lives in, so it must land
 * in the same root as the overlay. Called again at gesture start because the
 * host may not be connected to its final root when the directive first runs.
 */
function ensureSheet(host: HTMLElement): void {
	if (typeof document === 'undefined') return
	const root = host.getRootNode()
	const target: (ParentNode & Pick<Document, 'querySelector'>) | null =
		root instanceof ShadowRoot ? root : document.head
	if (!target || target.querySelector('#' + SHEET_ID)) return
	const style = document.createElement('style')
	style.id = SHEET_ID
	style.textContent = RING_CSS
	target.appendChild(style)
}

/** Inject a pointer-events-none overlay as the host's FIRST child (behind its content, so an interior fill stays readable). */
function injectRing(host: HTMLElement): RingHandle {
	ensureSheet(host)
	const root = document.createElement('div')
	root.className = 'schmancy-lp'
	Object.assign(root.style, {
		position: 'absolute',
		borderRadius: 'inherit',
		pointerEvents: 'none',
		opacity: '0',
		zIndex: '0',
		transition: 'opacity 150ms ease',
	})
	root.style.setProperty('inset', '0')
	root.style.setProperty('--lp-progress', '0')
	root.innerHTML =
		'<i class="schmancy-lp-aclip"><i class="schmancy-lp-aurora"></i></i>' +
		'<i class="schmancy-lp-track"></i><i class="schmancy-lp-comet"></i>' +
		'<i class="schmancy-lp-bloom"></i>'
	host.insertBefore(root, host.firstChild)

	let raf = 0
	let endTimer = 0
	const setP = (p: number) => root.style.setProperty('--lp-progress', String(p))
	const ramp = (to: number, ms: number) => {
		cancelAnimationFrame(raf)
		if (ms <= 0) return setP(to)
		const from = parseFloat(root.style.getPropertyValue('--lp-progress')) || 0
		const t0 = performance.now()
		const tick = () => {
			const k = Math.min(1, (performance.now() - t0) / ms)
			setP(from + (to - from) * k)
			if (k < 1) raf = requestAnimationFrame(tick)
		}
		raf = requestAnimationFrame(tick)
	}

	return {
		fill(ms) {
			ensureSheet(host) // host is connected to its final root by gesture time
			root.classList.remove('fire', 'pulse')
			root.style.opacity = '1'
			ramp(1, ms)
		},
		pulse() {
			cancelAnimationFrame(raf)
			root.style.opacity = '1'
			setP(1)
			root.classList.add('fire')
			window.clearTimeout(endTimer)
			endTimer = window.setTimeout(() => {
				root.classList.remove('fire')
				root.style.opacity = '0'
				setP(0)
			}, 560)
		},
		retract() {
			cancelAnimationFrame(raf)
			window.clearTimeout(endTimer)
			root.classList.remove('fire', 'pulse')
			root.style.opacity = '0'
			setP(0)
		},
		setReduced(reduced) {
			root.classList.toggle('reduced', reduced)
		},
		destroy() {
			cancelAnimationFrame(raf)
			window.clearTimeout(endTimer)
			root.remove()
		},
	}
}

class LongPressDirective extends AsyncDirective {
	/** The bound host — disconnected()/reconnected() receive no part. */
	private element: HTMLElement | null = null

	constructor(partInfo: PartInfo) {
		super(partInfo)
		if (partInfo.type !== PartType.ELEMENT) {
			throw new Error('longPress directive can only be used on elements')
		}
	}

	render(callback: () => void, options?: LongPressOptions) {
		void callback
		void options
		return undefined
	}

	update(part: ElementPart, [callback, options = {}]: [() => void, LongPressOptions?]) {
		const element = part.element as HTMLElement
		this.element = element
		const existing = longPressMap.get(element)

		// Arrow callbacks change identity every render — refresh the ref without
		// re-subscribing while the live setup still matches the options.
		if (existing && !existing.subscription.closed && this.optionsEqual(existing.options, options)) {
			existing.callback = callback
			return undefined
		}
		if (existing) this.teardown(existing)

		const record: LongPressState = {
			subscription: new Subscription(),
			callback,
			options,
			lastTapAt: 0,
			ring: null,
			restore: () => {},
		}
		longPressMap.set(element, record)
		if (this.isConnected) this.setup(element, record)
		else record.subscription.unsubscribe() // reconnected() sets it up
		return undefined
	}

	private optionsEqual(a: LongPressOptions, b: LongPressOptions | undefined): boolean {
		if (!b) return Object.keys(a).length === 0
		return (
			a.duration === b.duration &&
			a.movementThreshold === b.movementThreshold &&
			a.doubleClick === b.doubleClick &&
			a.doubleClickWindow === b.doubleClickWindow &&
			a.indicator === b.indicator
		)
	}

	private setup(element: HTMLElement, record: LongPressState): void {
		const root = record.subscription
		const indicatorOn = record.options.indicator ?? true

		// Suppress mobile double-tap-zoom; still allows pan/scroll.
		const prevTouchAction = element.style.touchAction
		element.style.touchAction = 'manipulation'
		// Ring is absolutely positioned to the host box — host must be a containing block.
		const prevPosition = element.style.position
		if (indicatorOn && getComputedStyle(element).position === 'static') element.style.position = 'relative'
		const ring = indicatorOn ? injectRing(element) : null
		record.ring = ring
		// Each element given the shortcut, with the value it had before.
		const advertised = new Map<Element, string | null>()
		record.restore = () => {
			element.style.touchAction = prevTouchAction
			element.style.position = prevPosition
			advertised.forEach((prev, target) =>
				prev === null ? target.removeAttribute('aria-keyshortcuts') : target.setAttribute('aria-keyshortcuts', prev),
			)
		}

		// Assistive tech reads aria-keyshortcuts off the focused element, so it goes
		// where focus lands: the host itself, or — for a delegatesFocus host such as
		// a filter chip, whose own box has no role — the control inside it. No
		// aria-haspopup: the act a consumer binds opens a dialog, and Enter stays
		// the host's.
		const advertise = (target: Element) => {
			if (advertised.has(target)) return
			const prev = target.getAttribute('aria-keyshortcuts')
			advertised.set(target, prev)
			const tokens = (prev ?? '').split(/\s+/).filter(Boolean)
			if (!tokens.includes(KEY_SHORTCUT)) target.setAttribute('aria-keyshortcuts', [...tokens, KEY_SHORTCUT].join(' '))
		}
		// A custom-element host has no shadow root until it connects, which is after this part commits.
		root.add(
			timer(0, asapScheduler)
				.pipe(filter(() => !element.shadowRoot?.delegatesFocus))
				.subscribe(() => advertise(element)),
		)
		root.add(
			fromEvent<FocusEvent>(element, 'focusin')
				.pipe(
					// Focus on the host or inside its own shadow tree — not a light-DOM control nested in it.
					filter(e => e.target === element),
					map(e => e.composedPath()[0]),
					filter((target): target is Element => target instanceof Element),
				)
				.subscribe(advertise),
		)

		// Live reduced-motion (the user can toggle mid-session — don't read .value once).
		// The ring renders a static variant under reduced motion; the gesture is unaffected.
		ring?.setReduced(reducedMotion$.value)
		root.add(reducedMotion$.subscribe(v => ring?.setReduced(v)))

		const fire = () => {
			record.lastTapAt = 0 // a fired act cancels any pending tap
			ring?.pulse()
			record.callback()
		}

		// A fired key or hold claims its platform echoes: the next `contextmenu`
		// and the next pointer `click` anywhere, until CLAIM_GRACE_MS after the
		// trigger is released. Each is swallowed wherever
		// it lands, since the act may have moved focus into a dialog by then. Any
		// pointerdown ends the claim, because what follows it is a new gesture — a
		// tap, a right-click, a ctrl-click, another hold.
		const claims = new Subject<Observable<unknown>>()
		root.add(
			claims
				.pipe(
					switchMap(released$ =>
						merge(
							fromEvent<MouseEvent>(window, 'contextmenu', { capture: true }).pipe(take(1)),
							// Enter and a scripted click() carry detail 0; only a pointer's click is the gesture's.
							fromEvent<MouseEvent>(window, 'click', { capture: true }).pipe(
								filter(e => e.detail > 0),
								take(1),
							),
						).pipe(
							takeUntil(
								merge(
									fromEvent(window, 'pointerdown', { capture: true }),
									released$.pipe(
										take(1),
										switchMap(() => timer(CLAIM_GRACE_MS)),
									),
								),
							),
						),
					),
				)
				.subscribe(e => {
					e.preventDefault()
					e.stopPropagation()
				}),
		)
		// Capture phase: an opened act that stops its keyup still releases the claim.
		const keyReleased$ = merge(fromEvent(window, 'keyup', { capture: true }), fromEvent(window, 'blur'))

		// At most one in-flight press; torn down when it ends, on the next press, or on root teardown.
		let press: Subscription | null = null
		// Fires the in-flight press as a hold — the platform's own long-touch menu may beat the timer.
		let claimPress: (() => void) | null = null
		root.add({ unsubscribe: () => press?.unsubscribe() })

		root.add(
			fromEvent<KeyboardEvent>(element, 'keydown')
				.pipe(
					// The host's own focus only — a control nested inside it keeps its menu key.
					filter(
						e =>
							e.target === element &&
							!e.ctrlKey &&
							!e.altKey &&
							!e.metaKey &&
							(e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)),
					),
					tap(e => e.preventDefault()),
					filter(e => !e.repeat),
				)
				.subscribe(() => {
					fire()
					claims.next(keyReleased$)
				}),
		)

		root.add(
			fromEvent<MouseEvent>(element, 'contextmenu')
				.pipe(tap(e => e.preventDefault()))
				.subscribe(() => (claimPress ? claimPress() : fire())),
		)

		root.add(
			fromEvent<PointerEvent>(element, 'pointerdown')
				// A secondary button is a context click — its `contextmenu` is the trigger.
				.pipe(filter(e => e.button === 0))
				.subscribe(downEv => {
					ensureSheet(element) // host is connected to its final root by gesture time
					press?.unsubscribe()
					const current = new Subscription()
					press = current

					const o = record.options
					const duration = o.duration ?? 500
					const threshold = o.movementThreshold ?? 10
					const doubleClick = o.doubleClick ?? true
					const dblWindow = o.doubleClickWindow ?? 400

					const startX = downEv.clientX
					const startY = downEv.clientY
					const startTime = performance.now()
					let firedHold = false
					const pressEnded$ = new Subject<void>()

					const fireHold = () => {
						firedHold = true
						fire()
						claims.next(pressEnded$)
					}
					claimPress = fireHold
					current.add(() => {
						if (claimPress === fireHold) claimPress = null
						pressEnded$.next()
						pressEnded$.complete()
					})
					const endPress = () => current.unsubscribe()

					current.add(
						timer(duration)
							.pipe(filter(() => !firedHold))
							.subscribe(fireHold),
					)

					// Hold indicator — paint after a beat so a scroll-swipe doesn't flash it.
					if (ring) {
						current.add(
							timer(120)
								.pipe(filter(() => !firedHold))
								.subscribe(() => ring.fill(Math.max(0, duration - 120))),
						)
					}

					current.add(
						fromEvent<PointerEvent>(window, 'pointermove')
							.pipe(
								// A fired hold is past cancelling; its press runs to the release, where the claim's grace starts.
								filter(() => !firedHold),
								filter(e => Math.hypot(e.clientX - startX, e.clientY - startY) > threshold),
								take(1),
							)
							.subscribe(() => {
								endPress()
								ring?.retract()
							}),
					)

					current.add(
						fromEvent<PointerEvent>(window, 'pointercancel')
							.pipe(take(1))
							.subscribe(() => {
								endPress()
								ring?.retract()
							}),
					)

					// Release → resolve hold vs tap vs double-tap.
					current.add(
						fromEvent<PointerEvent>(window, 'pointerup')
							.pipe(take(1))
							.subscribe(upEv => {
								const elapsed = performance.now() - startTime
								const movedFar = Math.hypot(upEv.clientX - startX, upEv.clientY - startY) > threshold
								endPress()
								if (!firedHold) ring?.retract()

								// A fired hold's release is not a tap; a moved/slow press is not a tap.
								if (firedHold || movedFar || elapsed >= duration) return
								if (!doubleClick) return

								const now = performance.now()
								if (record.lastTapAt && now - record.lastTapAt <= dblWindow) fire()
								else {
									// First tap of a potential double — record silently. No
									// indicator on a plain tap (a single click is just a select);
									// the ring is reserved for a deliberate hold.
									record.lastTapAt = now
									timer(dblWindow).subscribe(() => {
										if (record.lastTapAt === now) record.lastTapAt = 0
									})
								}
							}),
					)
				}),
		)
	}

	/** Undo setup on the host; the record stays so a reconnect can set it up again. */
	private teardown(state: LongPressState): void {
		state.subscription.unsubscribe()
		state.ring?.destroy()
		state.ring = null
		state.restore()
		state.restore = () => {}
	}

	protected override disconnected() {
		const state = this.element && longPressMap.get(this.element)
		if (state) this.teardown(state)
	}

	protected override reconnected() {
		const state = this.element && longPressMap.get(this.element)
		if (!this.element || !state?.subscription.closed) return
		state.subscription = new Subscription()
		this.setup(this.element, state)
	}
}

/**
 * Secondary-act directive for Lit components.
 *
 * Fires the callback on a press-and-hold (≥ `duration`), a double-click /
 * double-tap (two taps within `doubleClickWindow`), a right-click, or — while
 * the host itself has focus — `ContextMenu` or `Shift+F10`, suppressing the
 * browser's own menu. `aria-keyshortcuts="Shift+F10"` lands on whatever takes
 * focus for the host (the host, or the inner control of a delegatesFocus host);
 * its Enter is left alone. A platform `contextmenu` echoing a key or hold that
 * already fired (Windows' menu key, Android's long touch) is swallowed wherever
 * it lands, while a real right-click passes, so each gesture fires exactly once.
 * The click a firing hold generates is swallowed too, wherever it is released,
 * so a hold never also clicks the host; a tap, both taps of a double-tap,
 * Enter, and any later press still click it. Paints an in-element progress ring while a
 * hold builds; respects `prefers-reduced-motion`. Unbinding the directive
 * removes every listener and the shortcut it advertised.
 */
export const longPress = directive(LongPressDirective)
